import React, { useMemo } from 'react';
import { 
  Box,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  TextField
} from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { formatSchoolName } from '../../utils/nameFormatter';
import { getTravelModeOptions } from '../../constants/travelModes';
import { renderSchoolOption, filterSchoolOptions, getSchoolOptionLabel } from '../shared_components/schoolOption';

const DRIVE_TIMES = [5, 10, 15, 20, 25, 30];

const SchoolSummaryFilters = ({
  schools,
  selectedSchool,
  selectedDriveTime,
  selectedTravelMode = 'drive',
  availableDriveTimes = null,
  availableTravelModes = null,
  onSchoolChange,
  onDriveTimeChange,
  onTravelModeChange
}) => {
  const driveTimeOptions = availableDriveTimes || DRIVE_TIMES;
  const travelModeOptions = getTravelModeOptions(availableTravelModes);
  // Sort schools and memoize the result
  const sortedSchools = useMemo(() => {
    return [...schools].sort((a, b) => 
      formatSchoolName(a.name).localeCompare(formatSchoolName(b.name))
    );
  }, [schools]);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        backgroundColor: 'grey.50',
        backdropFilter: 'blur(8px)',
        zIndex: 1200,
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        py: 1.5,
        mb: 3,
        width: '100%',
        height: 'auto',
        minHeight: 'fit-content',
        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
      }}
    >
      <Container 
        maxWidth={false}
        sx={{
          px: { xs: 2, sm: 3 },
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <FilterList 
          color="primary" 
          sx={{ 
            opacity: 0.8,
            '&:hover': { opacity: 1 }
          }} 
        />
        
        <Grid container spacing={2} sx={{ flex: 1 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              options={sortedSchools}
              getOptionLabel={getSchoolOptionLabel}
              value={selectedSchool || null}
              disableClearable
              onChange={(event, newValue) => {
                if (newValue) onSchoolChange({ target: { value: newValue.uuid } });
              }}
              isOptionEqualToValue={(option, value) => option.uuid === value?.uuid}
              filterOptions={filterSchoolOptions}
              renderOption={(props, option) => renderSchoolOption(props, option, { dense: true })}
              ListboxProps={{ style: { maxHeight: 400 } }}
              renderInput={(params) => (
                <TextField {...params} label="School" placeholder="Search by name or location..." />
              )}
            />
          </Grid>

          <Grid item xs={12} md={onTravelModeChange ? 3 : 6}>
            <FormControl fullWidth size="small">
              <InputLabel>Travel Time</InputLabel>
              <Select
                value={selectedDriveTime}
                label="Travel Time"
                onChange={onDriveTimeChange}
              >
                {driveTimeOptions.map((time) => (
                  <MenuItem key={time} value={time}>
                    {time} minutes
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {onTravelModeChange && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select
                  value={selectedTravelMode}
                  label="Mode"
                  onChange={onTravelModeChange}
                >
                {travelModeOptions.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default SchoolSummaryFilters;